import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';

const POLL_INTERVAL_MS = 200;
const QUIET_TICKS_REQUIRED = 3;

/** True once Apollo's active queries have been quiet for QUIET_TICKS_REQUIRED consecutive polls — latches on.
 * `enabled: false` neither polls nor latches, so a caller behind a feature flag costs nothing while it is off. */
export function useApolloNetworkQuiet(enabled = true): boolean {
  const apolloClient = useApolloClient();
  const [isQuiet, setIsQuiet] = useState(false);

  useEffect(() => {
    if (!enabled || isQuiet) return;
    let cancelled = false;
    let quietTicks = 0;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    function tick() {
      if (cancelled) return;
      const anyLoading = Array.from(apolloClient.getObservableQueries('active').values()).some(
        (q) => q.getCurrentResult().loading,
      );
      if (anyLoading) {
        quietTicks = 0;
      } else {
        quietTicks += 1;
        if (quietTicks >= QUIET_TICKS_REQUIRED) {
          setIsQuiet(true);
          return;
        }
      }
      timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
    }
    tick();
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [apolloClient, enabled, isQuiet]);

  return isQuiet;
}
